// @ts-check
// Relatório da campanha de e-mail (spec/email.md seções 14 e 19.8; contrato 09 de e-mail, seção 16): abas Resumo,
// Destinatários, Links e Conteúdo. O resumo mostra o andamento (preparação, primeiro lote, limite diário, pausa com o
// motivo), os números com as taxas (aberturas sempre estimadas), os motivos de quem ficou de fora, os provedores e a
// linha do tempo. Atualiza a cada 5 s enquanto prepara ou envia e a cada 60 s nas primeiras 48 h depois, com a aba
// visível. Os destinatários fora das etapas do papel aparecem mascarados (a busca não os mostra).
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Link, searchParamsState, updateSearchParams } from '../../app/router.js';
import {
  cancelEmailCampaign, downloadEmailCampaignRecipientsCsv, getEmailCampaignContent, getEmailCampaignReport, listEmailCampaignLinks,
  listEmailCampaignRecipients, pauseEmailCampaign,
} from '../../data/email.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { downloadBlob } from '../../lib/csv.js';
import {
  canResumeEmailCampaign, displayedDelivered, formatRate, recipientDisplayStatus, recipientStatusTone, reportRefreshMilliseconds, shortenUrl,
} from '../../lib/email-campaigns.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { Button } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { TabList } from '../../ui/tab-list.js';
import { useDebouncedValue, useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { usePolling } from '../conversations/conversations-state.js';
import { EmailPreviewFrame, PreviewDeviceSwitch } from '../email-design/email-preview-frame.js';
import { ResumeEmailCampaignDialog } from './campaign-resume-dialog.js';
import { EMAIL_RECIPIENT_FILTERS } from '../../../../shared/email/email-settings.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email.js').EmailCampaign} EmailCampaign
 * @typedef {import('../../data/email.js').CampaignReport} CampaignReport
 * @typedef {import('../../data/email.js').CampaignRecipient} CampaignRecipient
 */

const REPORT_TABS = Object.freeze(/** @type {const} */ (['summary', 'recipients', 'links', 'content']));
// a aba Destinatários mostra estes filtros, na ordem da tela
const RECIPIENT_TAB_FILTERS = Object.freeze(['ALL', 'DELIVERED', 'OPENED', 'CLICKED', 'NOT_OPENED', 'BOUNCED', 'UNSUBSCRIBED', 'COMPLAINED', 'FAILED', 'SKIPPED', 'QUEUED']);

/** @param {number} value */
const percent = (value) => formatNumber(value, { percentage: true, decimals: 1 });

/** Texto de um motivo de quem ficou de fora (K22: um texto por regra). @param {string} reason */
export const deliveryReasonText = (reason) => (hasTranslation(`deliveryReasons.${reason}`) ? t(`deliveryReasons.${reason}`) : reason);

/** Texto do motivo da pausa: os do e-mail e os comuns aos dois canais. @param {string | null} reason @param {string} [memberName] */
export function pauseReasonText(reason, memberName = '') {
  const value = reason ?? 'MANUAL';
  if (value === 'MANUAL') return memberName ? t('emailCampaigns.pauseReasons.MANUAL', { name: memberName }) : t('emailCampaigns.pauseReasons.MANUAL_UNKNOWN');
  if (hasTranslation(`emailCampaigns.pauseReasons.${value}`)) return t(`emailCampaigns.pauseReasons.${value}`);
  if (hasTranslation(`campaignStatuses.pauseReasons.${value}`)) return t(`campaignStatuses.pauseReasons.${value}`);
  return t('emailCampaigns.pauseReasons.MANUAL_UNKNOWN');
}

/** Frase de um evento da linha do tempo. @param {{ kind: string, details: any }} event */
function eventText(event) {
  const key = hasTranslation(`emailCampaigns.report.events.${event.kind}`) ? event.kind : 'OTHER';
  const details = event.details ?? {};
  const base = t(`emailCampaigns.report.events.${key}`, {
    date: details.scheduledAt ? formatDateTime(details.scheduledAt) : '', eligible: formatNumber(details.eligible ?? 0),
  });
  if (event.kind === 'PAUSED' && details.reason) return `${base}: ${pauseReasonText(details.reason)}`;
  if (event.kind === 'FAILED' && details.reason) return `${base}: ${t(hasTranslation(`emailCampaigns.failureReasons.${details.reason}`) ? `emailCampaigns.failureReasons.${details.reason}` : 'emailCampaigns.failureReasons.UNKNOWN')}`;
  return base;
}

/**
 * Cartão de um número com a taxa.
 * @param {{ label: string, value: string, detail?: string | null, hint?: string | null, testId?: string, tone?: string }} props
 */
function StatCard({ label, value, detail, hint, testId, tone }) {
  return html`<div class="ec-stat" data-testid=${testId} data-tone=${tone}>
    <span class="ec-stat-label">${label}${hint ? html`<span class="ec-hint-icon" title=${hint} aria-label=${hint} role="img"><${Icon} name="IconInfoCircle" size=${14} /></span>` : null}</span>
    <span class="ec-stat-value">${value}</span>
    ${detail ? html`<span class="ec-stat-detail">${detail}</span>` : null}
  </div>`;
}

/**
 * Faixa de andamento e da pausa, com os botões que a situação permite.
 * @param {{ report: CampaignReport, onChanged: () => void }} props
 */
function StatusBand({ report, onChanged }) {
  const { campaign, progress } = report;
  const [confirm, setConfirm] = useState(/** @type {'cancel' | 'resume' | null} */ (null));
  const [isBusy, setIsBusy] = useState(false);
  const run = async (/** @type {() => Promise<unknown>} */ action, /** @type {string} */ message) => {
    setIsBusy(true);
    try {
      await action();
      showSuccessSnackbar(message);
      setConfirm(null);
      onChanged();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };
  const isActive = ['PREPARING', 'SENDING', 'PAUSED'].includes(campaign.status);
  /** @type {string[]} */
  const lines = [];
  if (campaign.status === 'PREPARING') {
    lines.push(campaign.progress.totalCards
      ? t('emailCampaigns.report.preparing', { done: formatNumber(campaign.progress.preparedCards), total: formatNumber(campaign.progress.totalCards) })
      : t('emailCampaigns.report.preparingUnknown'));
  }
  if (campaign.status === 'SENDING' && campaign.sendPhase === 'WARMUP_HOLD' && campaign.warmupHoldUntil) {
    lines.push(t('emailCampaigns.report.warmup', { time: formatDateTime(campaign.warmupHoldUntil) }));
  }
  if (campaign.status === 'SENDING' && campaign.waitingUntil) lines.push(t('emailCampaigns.report.waitingDailyLimit', { date: formatDateTime(campaign.waitingUntil) }));
  if (campaign.status === 'PAUSED') lines.push(pauseReasonText(campaign.pauseReason));
  if (['SENT', 'SENT_WITH_ERRORS', 'CANCELED'].includes(campaign.status) && campaign.sentAt) lines.push(t('emailCampaigns.report.finishedAt', { date: formatDateTime(campaign.sentAt) }));
  else if (campaign.startedAt && isActive) lines.push(t('emailCampaigns.report.startedAt', { date: formatDateTime(campaign.startedAt) }));
  const share = progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
  return html`<section class="ec-status-band" data-status=${campaign.status} data-testid="email-campaign-status-band">
    ${progress.total > 0 && campaign.status !== 'PREPARING'
      ? html`<div class="ec-progress">
          <div class="ec-progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax=${progress.total} aria-valuenow=${progress.done}>
            <span style=${{ width: `${share}%` }}></span>
          </div>
          <span class="ec-progress-text">${t('emailCampaigns.report.progress', { done: formatNumber(progress.done), total: formatNumber(progress.total) })}</span>
        </div>`
      : null}
    ${lines.map((line) => html`<p class="ec-status-line">${line}</p>`)}
    ${isActive
      ? html`<div class="ec-button-row">
          ${campaign.status === 'SENDING'
            ? html`<${Button} title=${t('emailCampaigns.actions.pause')} icon="IconPlayerPause" size="small" variant="secondary" isLoading=${isBusy}
                onClick=${() => run(() => pauseEmailCampaign(campaign.id), t('emailCampaigns.toasts.paused'))} />`
            : null}
          ${canResumeEmailCampaign(campaign)
            ? html`<${Button} title=${t('emailCampaigns.actions.resume')} icon="IconPlayerPlay" size="small" accent="blue" onClick=${() => setConfirm('resume')}
                data-testid="email-campaign-resume" />`
            : null}
          <${Button} title=${t('emailCampaigns.actions.cancel')} icon="IconPlayerStop" size="small" variant="secondary" accent="danger" onClick=${() => setConfirm('cancel')} />
        </div>`
      : null}
    ${confirm === 'cancel'
      ? html`<${Modal} title=${t('emailCampaigns.confirm.cancelTitle', { name: campaign.name })} subtitle=${t('emailCampaigns.confirm.cancelDescription')}
          confirmLabel=${t('emailCampaigns.actions.cancel')} confirmAccent="danger" isLoading=${isBusy} onClose=${() => setConfirm(null)}
          onConfirm=${() => run(() => cancelEmailCampaign(campaign.id), t('emailCampaigns.toasts.canceled'))} />`
      : null}
    ${confirm === 'resume'
      ? html`<${ResumeEmailCampaignDialog} campaign=${campaign} onClose=${() => setConfirm(null)} onResumed=${() => {
          setConfirm(null);
          onChanged();
        }} />`
      : null}
  </section>`;
}

/** Aba Resumo. @param {{ report: CampaignReport, onChanged: () => void }} props */
function SummaryTab({ report, onChanged }) {
  const { counts, rates } = report;
  const [isSkippedOpen, setIsSkippedOpen] = useState(false);
  const notMeasured = report.legacyMetrics;
  const estimated = report.isDeliveryEstimated ? ` ${t('emailCampaigns.report.estimated')}` : '';
  const delivered = displayedDelivered({ counts, rates });
  const skippedReasons = Object.entries(counts.skippedByReason ?? {}).sort((first, second) => second[1] - first[1]);
  return html`<div class="ec-summary">
    <${StatusBand} report=${report} onChanged=${onChanged} />
    ${notMeasured ? html`<p class="ec-notice" data-tone="gray"><${Icon} name="IconInfoCircle" size=${16} /><span>${t('emailCampaigns.report.legacyNotice')}</span></p>` : null}
    <div class="ec-stats">
      <${StatCard} testId="email-report-recipients" label=${t('emailCampaigns.report.cards.recipients')} value=${formatNumber(counts.recipients)}
        detail=${counts.queued > 0 ? `${t('emailCampaigns.report.cards.queued')}: ${formatNumber(counts.queued)}` : null} />
      <${StatCard} testId="email-report-delivered" label=${`${t('emailCampaigns.report.cards.delivered')}${estimated}`}
        value=${notMeasured ? '-' : formatNumber(delivered)}
        detail=${notMeasured ? t('emailCampaigns.report.notMeasured') : t('emailCampaigns.report.rateOfSent', { rate: formatRate(rates.deliveryRate, percent) })} />
      <${StatCard} testId="email-report-opened" label=${t('emailCampaigns.report.cards.opened')} hint=${t('emailCampaigns.report.cards.openedHint')}
        value=${notMeasured ? '-' : formatNumber(counts.opened)}
        detail=${notMeasured ? t('emailCampaigns.report.notMeasured') : [
          t('emailCampaigns.report.rateOfDelivered', { rate: formatRate(rates.openRate, percent) }),
          report.machineOpenShare ? t('emailCampaigns.report.cards.machineShare', { share: formatRate(report.machineOpenShare, percent) }) : '',
        ].filter(Boolean).join(' · ')} />
      <${StatCard} testId="email-report-clicked" label=${t('emailCampaigns.report.cards.clicked')} value=${notMeasured ? '-' : formatNumber(counts.clicked)}
        detail=${notMeasured ? t('emailCampaigns.report.notMeasured') : [
          t('emailCampaigns.report.rateOfDelivered', { rate: formatRate(rates.clickRate, percent) }),
          rates.clickToOpenRate !== null ? t('emailCampaigns.report.cards.clickToOpen', { rate: formatRate(rates.clickToOpenRate, percent) }) : '',
        ].filter(Boolean).join(' · ')} />
      <${StatCard} testId="email-report-unsubscribed" label=${t('emailCampaigns.report.cards.unsubscribed')} value=${formatNumber(counts.unsubscribed)}
        detail=${t('emailCampaigns.report.rateOfDelivered', { rate: formatRate(rates.unsubscribeRate, percent) })} />
      <${StatCard} testId="email-report-bounced" label=${t('emailCampaigns.report.cards.bounced')} value=${formatNumber(counts.bounced)}
        tone=${(rates.hardBounceRate ?? 0) >= 0.05 ? 'red' : undefined}
        detail=${t('emailCampaigns.report.cards.bouncedDetail', { hard: formatNumber(counts.hardBounced), soft: formatNumber(counts.softBounced) })} />
      <${StatCard} testId="email-report-complained" label=${t('emailCampaigns.report.cards.complained')} value=${formatNumber(counts.complained)}
        tone=${counts.complained > 0 ? 'orange' : undefined}
        detail=${t('emailCampaigns.report.rateOfDelivered', { rate: formatRate(rates.complaintRate, percent) })} />
      <${StatCard} testId="email-report-failed" label=${t('emailCampaigns.report.cards.failed')} value=${formatNumber(counts.failed)} />
      <div class="ec-stat" data-testid="email-report-skipped">
        <span class="ec-stat-label">${t('emailCampaigns.report.cards.skipped')}</span>
        <span class="ec-stat-value">${formatNumber(counts.skipped)}</span>
        ${skippedReasons.length > 0
          ? html`<button type="button" class="ec-link-button" aria-expanded=${isSkippedOpen ? 'true' : 'false'} onClick=${() => setIsSkippedOpen((open) => !open)}>
              ${t('emailCampaigns.report.cards.skippedOpen')}</button>`
          : null}
      </div>
    </div>
    ${isSkippedOpen && skippedReasons.length > 0
      ? html`<ul class="ec-reasons" data-testid="email-report-skipped-reasons">
          ${skippedReasons.map(([reason, total]) => html`<li key=${reason}><${Icon} name="IconForbid2" size=${12} /><span>${deliveryReasonText(reason)}</span><strong>${formatNumber(total)}</strong></li>`)}
        </ul>`
      : null}
    ${report.byProvider.length > 0
      ? html`<section class="ec-section">
          <h3 class="ec-section-title">${t('emailCampaigns.report.byProvider')}</h3>
          <div class="ec-mini-table" role="table">
            <div class="ec-mini-row ec-mini-header" role="row">
              ${['provider', 'sent', 'delivered', 'opened', 'clicked'].map((column) => html`<span role="columnheader">${t(`emailCampaigns.report.providerColumns.${column}`)}</span>`)}
            </div>
            ${report.byProvider.map((item) => html`<div key=${item.provider} class="ec-mini-row" role="row">
              <span role="cell">${t(`emailCampaigns.report.providers.${item.provider}`)}</span>
              <span role="cell">${formatNumber(item.sent)}</span><span role="cell">${formatNumber(item.delivered)}</span>
              <span role="cell">${formatNumber(item.opened)}</span><span role="cell">${formatNumber(item.clicked)}</span>
            </div>`)}
          </div>
        </section>`
      : null}
    <section class="ec-section">
      <h3 class="ec-section-title">${t('emailCampaigns.report.timelineTitle')}</h3>
      <ol class="ec-events">
        ${report.events.map((event, index) => html`<li key=${`${event.kind}-${event.occurredAt}-${index}`}>
          <span class="ec-event-time">${formatDateTime(event.occurredAt)}</span><span>${eventText(event)}</span>
        </li>`)}
      </ol>
    </section>
  </div>`;
}

/** Aba Destinatários. @param {{ campaign: EmailCampaign }} props */
function RecipientsTab({ campaign }) {
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [rows, setRows] = useState(/** @type {CampaignRecipient[] | null} */ (null));
  const [cursor, setCursor] = useState(/** @type {string | null} */ (null));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const requestRef = useRef(0);
  const canExport = hasCurrentPermissionFlag('EXPORT_CSV');

  useEffect(() => {
    requestRef.current += 1;
    const request = requestRef.current;
    setRows(null);
    listEmailCampaignRecipients(campaign.id, { status: filter, search: debouncedSearch })
      .then((page) => {
        if (request !== requestRef.current) return;
        setRows(page.recipients);
        setCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
      })
      .catch((error) => {
        if (request === requestRef.current) setRows([]);
        showErrorSnackbar(error);
      });
  }, [campaign.id, filter, debouncedSearch]);

  const loadMore = async () => {
    if (!cursor) return;
    setIsLoadingMore(true);
    try {
      const page = await listEmailCampaignRecipients(campaign.id, { status: filter, search: debouncedSearch, startingAfter: cursor });
      setRows((current) => [...(current ?? []), ...page.recipients]);
      setCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const file = await downloadEmailCampaignRecipientsCsv(campaign.id, filter);
      downloadBlob(file.blob, file.fileName ?? 'destinatarios.csv');
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsExporting(false);
    }
  };

  /** @param {CampaignRecipient} recipient */
  const nameCell = (recipient) => {
    if (recipient.isOutOfScope) return html`<span class="ec-muted ec-masked">${t('emailCampaigns.report.outOfScope')}</span>`;
    const name = recipient.displayName || recipient.email || '';
    if (recipient.recordId) return html`<${Link} href=${`/object/enrollmentOpportunity/${recipient.recordId}`} className="ec-link">${name}<//>`;
    if (recipient.personId) return html`<${Link} href=${`/object/person/${recipient.personId}`} className="ec-link">${name}<//>`;
    return html`<span>${name}</span>`;
  };
  /** @param {CampaignRecipient} recipient */
  const statusCell = (recipient) => {
    const status = recipientDisplayStatus(recipient);
    const reason = recipient.skipReason ? deliveryReasonText(recipient.skipReason) : null;
    return html`<span class="ec-recipient-status">
      <span class="ec-badge" data-tone=${recipientStatusTone(status)}>${t(`emailCampaigns.report.recipientStatus.${status}`)}</span>
      ${reason ? html`<span class="ec-muted">${reason}</span>` : null}
    </span>`;
  };

  return html`<div class="ec-recipients" data-testid="email-campaign-recipients">
    <div class="ec-toolbar">
      <label class="ec-search">
        <${Icon} name="IconSearch" size=${14} />
        <input type="search" value=${search} placeholder=${t('emailCampaigns.report.searchRecipients')} aria-label=${t('emailCampaigns.report.searchRecipients')}
          onInput=${(/** @type {Event} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      ${canExport ? html`<${Button} title=${t('emailCampaigns.report.exportCsv')} icon="IconDownload" size="small" variant="secondary" isLoading=${isExporting} onClick=${exportCsv} />` : null}
    </div>
    <div class="ec-filter-chips" role="radiogroup" aria-label=${t('emailCampaigns.report.tabs.recipients')}>
      ${RECIPIENT_TAB_FILTERS.filter((option) => EMAIL_RECIPIENT_FILTERS.includes(option)).map((option) => html`<button key=${option} type="button" role="radio"
        class="ec-filter-chip" aria-checked=${filter === option ? 'true' : 'false'} onClick=${() => setFilter(option)}>${t(`emailCampaigns.report.recipientFilters.${option}`)}</button>`)}
    </div>
    ${rows === null
      ? html`<div class="ec-skeletons">${Array.from({ length: 5 }, (_, index) => html`<${Skeleton} key=${index} height=${40} radius=${6} />`)}</div>`
      : rows.length === 0
        ? html`<p class="ec-empty-filter">${t('emailCampaigns.report.emptyFilter')}</p>`
        : isMobile
          ? html`<ul class="ec-recipient-cards">${rows.map((recipient) => html`<li key=${recipient.id} class="ec-recipient-card" data-masked=${recipient.isOutOfScope ? 'true' : undefined}>
              <div>${nameCell(recipient)}</div>
              ${recipient.email ? html`<div class="ec-muted ec-break">${recipient.email}</div>` : null}
              <div>${statusCell(recipient)}</div>
            </li>`)}</ul>`
          : html`<div class="ec-table ec-recipient-table" role="table">
              <div class="ec-table-row ec-table-header" role="row">
                ${['name', 'email', 'status', 'opened', 'clicked', 'date'].map((column) => html`<span role="columnheader">${t(`emailCampaigns.report.recipientColumns.${column}`)}</span>`)}
              </div>
              ${rows.map((recipient) => html`<div key=${recipient.id} class="ec-table-row" role="row" data-masked=${recipient.isOutOfScope ? 'true' : undefined}>
                <span role="cell">${nameCell(recipient)}</span>
                <span role="cell" class="ec-muted ec-break">${recipient.email ?? ''}</span>
                <span role="cell">${statusCell(recipient)}</span>
                <span role="cell" class="ec-muted">${recipient.firstOpenedAt ? formatDateTime(recipient.firstOpenedAt) : '-'}</span>
                <span role="cell" class="ec-muted">${recipient.firstClickedAt ? formatDateTime(recipient.firstClickedAt) : '-'}</span>
                <span role="cell" class="ec-muted">${recipient.sentAt ? formatDateTime(recipient.sentAt) : '-'}</span>
              </div>`)}
            </div>`}
    ${cursor ? html`<div class="ec-load-more"><${Button} title=${t('emailCampaigns.report.loadMore')} size="small" variant="secondary" isLoading=${isLoadingMore} onClick=${loadMore} /></div>` : null}
  </div>`;
}

/** Aba Links. @param {{ campaign: EmailCampaign }} props */
function LinksTab({ campaign }) {
  const [links, setLinks] = useState(/** @type {import('../../data/email.js').CampaignLink[] | null} */ (null));
  useEffect(() => {
    listEmailCampaignLinks(campaign.id).then(setLinks).catch((error) => {
      setLinks([]);
      showErrorSnackbar(error);
    });
  }, [campaign.id]);
  if (links === null) return html`<${Skeleton} height=${120} radius=${8} />`;
  if (links.length === 0) return html`<p class="ec-empty-filter">${t('emailCampaigns.report.links.empty')}</p>`;
  return html`<div class="ec-table ec-links-table" role="table" data-testid="email-campaign-links">
    <div class="ec-table-row ec-table-header" role="row">
      ${['label', 'url', 'unique', 'total'].map((column) => html`<span role="columnheader" class=${column === 'unique' || column === 'total' ? 'ec-number' : ''}>
        ${t(`emailCampaigns.report.links.${column}`)}</span>`)}
    </div>
    ${links.map((link) => html`<div key=${link.index} class="ec-table-row" role="row">
      <span role="cell">${link.label || '-'}</span>
      <span role="cell" class="ec-muted ec-break" title=${link.url}>${shortenUrl(link.url)}</span>
      <span role="cell" class="ec-number">${formatNumber(link.uniqueClicks)}</span>
      <span role="cell" class="ec-number">${formatNumber(link.totalClicks)}</span>
    </div>`)}
  </div>`;
}

/** Aba Conteúdo: o que saiu, com os dados de exemplo. @param {{ campaign: EmailCampaign }} props */
function ContentTab({ campaign }) {
  const [message, setMessage] = useState(/** @type {any} */ (null));
  const [failed, setFailed] = useState(false);
  const [device, setDevice] = useState(/** @type {'DESKTOP' | 'MOBILE' | 'TEXT'} */ ('DESKTOP'));
  useEffect(() => {
    getEmailCampaignContent(campaign.id).then(setMessage).catch(() => setFailed(true));
  }, [campaign.id]);
  if (failed) {
    return campaign.legacy
      ? html`<pre class="email-preview-text" data-testid="email-campaign-legacy-body">${campaign.legacy.bodyText}</pre>`
      : html`<p class="ec-inline-error" role="alert">${t('emailCampaigns.report.loadError')}</p>`;
  }
  if (!message) return html`<${Skeleton} height=${320} radius=${8} />`;
  return html`<div class="ec-content-tab" data-testid="email-campaign-content">
    <p class="ec-muted">${t('emailCampaigns.report.contentFrozen')}</p>
    <${PreviewDeviceSwitch} device=${device} onChange=${setDevice} />
    <${EmailPreviewFrame} message=${message} device=${device} senderName=${campaign.fromName ?? ''} />
  </div>`;
}

/**
 * @param {{ campaign: EmailCampaign, onCampaignChange: (campaign: EmailCampaign) => void }} props
 */
export function EmailCampaignReport({ campaign, onCampaignChange }) {
  const [report, setReport] = useState(/** @type {CampaignReport | null} */ (null));
  const [failed, setFailed] = useState(false);
  const parameters = searchParamsState.value;
  const tab = /** @type {typeof REPORT_TABS[number]} */ (REPORT_TABS.includes(/** @type {any} */ (parameters.get('tab'))) ? parameters.get('tab') : 'summary');

  const load = () => getEmailCampaignReport(campaign.id)
    .then((loaded) => {
      setReport(loaded);
      setFailed(false);
      if (loaded.campaign.status !== campaign.status || loaded.campaign.updatedAt !== campaign.updatedAt) onCampaignChange(loaded.campaign);
    })
    .catch(() => setFailed(true));
  useEffect(() => {
    load();
  }, [campaign.id]);
  const interval = reportRefreshMilliseconds(report?.campaign ?? campaign);
  usePolling(load, interval ?? 60_000, interval !== null);

  if (failed && !report) {
    return html`<div class="ec-inline-error" role="alert"><span>${t('emailCampaigns.report.loadError')}</span>
      <${Button} title=${t('emailCampaigns.retry')} size="small" variant="secondary" onClick=${load} /></div>`;
  }
  return html`<div class="ec-report" data-testid="email-campaign-report">
    <${TabList} tabs=${REPORT_TABS.map((id) => ({ id, title: t(`emailCampaigns.report.tabs.${id}`) }))} activeTabId=${tab}
      onChange=${(/** @type {string} */ id) => updateSearchParams({ tab: id === 'summary' ? null : id })} />
    <div class="ec-report-body">
      ${tab === 'summary'
        ? report
          ? html`<${SummaryTab} report=${report} onChanged=${load} />`
          : html`<div class="ec-stats">${Array.from({ length: 8 }, (_, index) => html`<${Skeleton} key=${index} height=${88} radius=${8} />`)}</div>`
        : null}
      ${tab === 'recipients' ? html`<${RecipientsTab} campaign=${campaign} />` : null}
      ${tab === 'links' ? html`<${LinksTab} campaign=${campaign} />` : null}
      ${tab === 'content' ? html`<${ContentTab} campaign=${campaign} />` : null}
    </div>
  </div>`;
}

